import { type ReactNode, useState } from 'react';
import { type Avatar, SLOTS, type Slot, slotLabel } from '../models/types';
import { catalog } from '../state/catalog';
import { type SlotAnchor, FRONT_ONLY, MIRRORED_ON_BACK, avatarBodyFor } from './bodies';

/** How an item is drawn on the avatar. Only emoji exist today; per-item art can arrive as another variant. */
function resolveGlyph(itemId: string): string | null {
  return catalog.contains(itemId) ? catalog.get(itemId).emoji : null;
}

const MIN_TARGET = 0.16;

interface Props {
  avatar: Avatar;
  size?: number;
  /** Show a flip button to see the back (items worn on the back). */
  flippable?: boolean;
  /** Every part of the avatar reports the slot that was tapped. */
  onSlotTap?: (slot: Slot) => void;
}

/**
 * The avatar: its base body with each equipped item's glyph at that body's anchor for the
 * slot, drawn back to front. Placed items carry `data-testid="avatar-slot-<slot>"`, except
 * shoes, which come as a pair (`avatar-slot-feet-left` / `-right`, facing opposite ways).
 */
export function AvatarView({ avatar, size = 280, flippable = false, onSlotTap }: Props) {
  const [back, setBack] = useState(false);
  const body = avatarBodyFor(avatar.body);
  const isLegWear = (slot: Slot) => slot === 'body' && !!avatar.equipped.body && body.legWear.has(avatar.equipped.body);
  const visible = SLOTS.filter((s) => !back || !FRONT_ONLY.has(s) || isLegWear(s));

  const anchor = (slot: Slot): SlotAnchor | undefined => {
    const a = isLegWear(slot) ? body.legsAnchor : body.anchors[slot];
    if (!a) return undefined;
    return back && MIRRORED_ON_BACK.has(slot) ? { ...a, x: 1 - a.x } : a;
  };

  /** Position a `side`-sized (fraction of the canvas) square centred on the anchor, in percent. */
  const box = (a: SlotAnchor, side: number) => ({
    left: `${(a.x - side / 2) * 100}%`,
    top: `${(a.y - side / 2) * 100}%`,
    width: `${side * 100}%`,
    height: `${side * 100}%`,
  });

  const glyph = (testId: string, a: SlotAnchor, emoji: string, mirror = false): ReactNode => {
    return (
      <span
        key={testId}
        className="glyph"
        data-testid={testId}
        style={{ ...box(a, a.size), fontSize: `${a.size * 85}cqw`, transform: mirror ? 'scaleX(-1)' : undefined }}
      >
        {emoji}
      </span>
    );
  };

  const place = (slot: Slot): ReactNode[] => {
    const itemId = avatar.equipped[slot];
    const emoji = itemId ? resolveGlyph(itemId) : null;
    if (!emoji) return [];
    if (slot === 'feet') {
      // Shoe glyphs point left. The right foot is mirrored so the pair face opposite ways.
      return [glyph('avatar-slot-feet-left', body.footAnchors.left, emoji), glyph('avatar-slot-feet-right', body.footAnchors.right, emoji, true)];
    }
    const a = anchor(slot);
    return a ? [glyph(`avatar-slot-${slot}`, a, emoji)] : [];
  };

  const hitZones = (slot: Slot): ReactNode[] => {
    const anchors: { a: SlotAnchor; suffix: string }[] =
      slot === 'feet'
        ? [
            { a: body.footAnchors.left, suffix: '-left' },
            { a: body.footAnchors.right, suffix: '-right' },
          ]
        : slot === 'back' && !back // Seen from the front the back is behind the torso, so there is nothing to tap.
          ? []
          : anchor(slot)
            ? [{ a: anchor(slot)!, suffix: '' }]
            : [];
    return anchors.map(({ a, suffix }) => {
      const side = Math.max(a.size, MIN_TARGET);
      return (
        <button
          key={`${slot}${suffix}`}
          type="button"
          className="hit"
          data-testid={`avatar-hit-${slot}${suffix}`}
          aria-label={`${slotLabel(slot)}${suffix ? ` (${suffix.slice(1)})` : ''}`}
          style={box(a, side)}
          onClick={() => onSlotTap?.(slot)}
        />
      );
    });
  };

  const backItem = place('back');
  return (
    <div className="avatar" style={{ width: `min(${size}px, 100%)`, aspectRatio: '1' }} data-testid="avatar-view" data-side={back ? 'back' : 'front'}>
      {!back && backItem}
      <svg viewBox="0 0 1 1" aria-hidden="true">{body.draw(back ? 'back' : 'front')}</svg>
      {back && backItem}
      {visible.filter((s) => s !== 'back').flatMap(place)}
      {onSlotTap && visible.flatMap(hitZones)}
      {flippable && (
        <button type="button" className="icon-btn flip" data-testid="avatar-flip" aria-label={back ? 'Show front' : 'Show back'} title={back ? 'Show front' : 'Show back'} onClick={() => setBack((b) => !b)}>
          ⇄
        </button>
      )}
    </div>
  );
}
